import React, { useState, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Share2, Check, QrCode, X, Download, ImageIcon } from 'lucide-react';
import { canonicalUrl } from '../utils/canonicalUrl';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

/**
 * Country + language locked share row.
 *
 * Previously this UI was buried inside the expanded `<HowToGuide>` panel,
 * meaning users only saw it after opening the collapsible "How to" box.
 * It now lives next to the main `<ShareButtons />` row so the "Share
 * locked to AE / AR" pill, the QR popover and the copy-link button are
 * always visible without an extra click.
 *
 * The URL it produces is ALWAYS pinned to https://zakat.international —
 * never the preview/dev origin — so QR codes printed for masjid notice
 * boards and links shared on WhatsApp/Telegram point to the live product.
 */
export const LocalizedShareRow = ({
  selectedCountry,
  selectedLanguage,
  testid = 'localized-share',
}) => {
  const { t } = useZakatGlobal();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const cc = selectedCountry?.code || 'US';
  const lc = selectedLanguage?.code || 'en';
  const deeplink = useMemo(
    () => canonicalUrl({ country: cc, lang: lc }),
    [cc, lc]
  );

  const copyDeeplink = async () => {
    try {
      await navigator.clipboard.writeText(deeplink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try { window.prompt('Copy this link:', deeplink); } catch { /* ignore */ }
    }
  };

  const downloadQrPng = () => {
    try {
      const svg = document.querySelector(`[data-testid="${testid}-qr-svg"]`);
      if (!svg) return;
      const xml = new XMLSerializer().serializeToString(svg);
      const img = new Image();
      const url = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512; canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 512, 512);
        ctx.drawImage(img, 0, 0, 512, 512);
        const pngUrl = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `zakat-international-${cc}-${lc}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };
      img.src = url;
    } catch { /* ignore */ }
  };

  const downloadPoster = () => {
    const a = document.createElement('a');
    a.href = '/share-poster.png';
    a.download = 'zakat-international-poster.png';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="inline-flex items-center gap-1 sm:gap-1.5 flex-nowrap shrink-0" data-testid={`${testid}-row`}>
      <button
        type="button"
        onClick={downloadPoster}
        data-testid={`${testid}-poster`}
        aria-label="Download shareable poster"
        title="Download shareable poster"
        className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-gold-500/60 bg-gold-50 text-[#4A5568] hover:bg-gold-100 hover:border-gold-500 transition-colors shrink-0"
      >
        <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </button>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testid}-qr`}
        aria-label="Show QR code for this country + language combination"
        title="Show QR code"
        className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-stone-200 bg-white text-[#0D1520] hover:bg-[#F3EFEA] hover:border-[#0D4733] transition-colors shrink-0"
      >
        <QrCode className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </button>
      <button
        type="button"
        onClick={copyDeeplink}
        data-testid={`${testid}-copy`}
        title={t('share.localizedCta')}
        className="inline-flex items-center gap-1.5 h-7 sm:h-8 px-2 sm:px-2.5 rounded-full text-[11px] font-semibold bg-[#EDF5F1] text-[#0D1520] hover:bg-[#EDF5F1] transition-colors shrink-0"
      >
        {copied ? <><Check className="w-3 h-3" /><span className="hidden sm:inline">{t('share.copied')}</span></> : <><Share2 className="w-3 h-3" /><span className="hidden sm:inline">{t('share.localizedCta')}</span></>}
      </button>

      {/* QR popover */}
      {open && (
        <div
          data-testid={`${testid}-qr-modal`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(43,36,25,0.35)] backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 border border-[#E2DCD5]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-qr-close`}
              aria-label="Close QR code"
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-700"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-center">
              <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-2">Scan to open · {selectedCountry?.flag} {cc} / {lc.toUpperCase()}</div>
              <div className="flex justify-center bg-white p-2 rounded-lg">
                <QRCodeSVG
                  value={deeplink}
                  size={220}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#0D1520"
                  data-testid={`${testid}-qr-svg`}
                />
              </div>
              <div className="mt-3 text-[10px] font-mono text-stone-500 break-all px-1" data-testid={`${testid}-qr-url`}>{deeplink}</div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <button
                  type="button"
                  onClick={downloadPoster}
                  data-testid={`${testid}-qr-poster`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-gold-500 text-[#0D1520] hover:bg-gold-400"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> Download poster
                </button>
                <button
                  type="button"
                  onClick={downloadQrPng}
                  data-testid={`${testid}-qr-download`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-[#EDF5F1] text-[#0D1520] hover:bg-[#EDF5F1]"
                >
                  <Download className="w-3.5 h-3.5" /> Download QR
                </button>
                <button
                  type="button"
                  onClick={copyDeeplink}
                  data-testid={`${testid}-qr-copy`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-white border border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA]"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Share2 className="w-3.5 h-3.5" />Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalizedShareRow;
